import { competitionMachine, } from './states.js';
/**
 * Concours (cahier §3, TODO phase 6) : règles partagées entre l'API (qui décide)
 * et les portails (qui affichent). Le portail n'est jamais la source de décision
 * sur l'ouverture des dépôts (cahier §3.3) : il affiche la fenêtre calculée par l'API.
 */
export const COMPETITION_TYPES = {
    direct: 'Concours direct',
    professional: 'Concours professionnel',
    internal: 'Concours interne',
    other: 'Autre catégorie autorisée',
};
export const PAYMENT_TIMING_LABELS = {
    before_submit: 'Avant la soumission du dossier',
    after_submit: 'Après la soumission, avant la date limite de paiement',
    after_admissibility: 'Après la recevabilité du dossier',
    not_required: 'Aucun frais',
};
export const CURRENCIES = ['XOF'];
/** Référence métier : majuscules, chiffres et tirets (ex. PEN-2026-AGENTS). */
export const COMPETITION_REFERENCE_REGEX = /^[A-Z0-9][A-Z0-9-]{2,39}$/;
/**
 * Champs figés une fois le concours publié (équité vis-à-vis des candidats,
 * matricules déjà annoncés). La date de clôture ne change ensuite que par
 * prolongation motivée ; l'ouverture n'est plus modifiable une fois ouverte.
 */
export const COMPETITION_LOCKED_AFTER_PUBLICATION = [
    'reference',
    'year',
    'type',
    'applicationPrefix',
    'paymentTiming',
    'feeAmount',
    'currency',
    'eligibility',
];
export const COMPETITION_LOCKED_ONCE_OPEN = ['opensAt', 'closesAt'];
/** Transitions réservées à d'autres modules (publication des résultats : phase 15). */
export const COMPETITION_TRANSITIONS_ELSEWHERE = [
    'provisional_results',
    'final_results',
];
export const COMPETITION_PHASE_LABELS = {
    unavailable: 'Non publié',
    upcoming: 'À venir',
    open: 'Dépôts ouverts',
    suspended: 'Dépôts suspendus',
    closed: 'Dépôts clos',
    results: 'Résultats',
};
const toDate = (v) => (v ? new Date(v) : null);
/**
 * Phase effective d'un concours à l'instant `now`. Le statut ne suffit pas :
 * un concours « ouvert » dont la date de clôture est passée (tâche planifiée
 * en retard) est considéré clos.
 */
export function competitionWindow(c, now = new Date()) {
    const opens = toDate(c.opensAt);
    const closes = toDate(c.closesAt);
    let phase;
    switch (c.status) {
        case 'draft':
        case 'archived':
            phase = 'unavailable';
            break;
        case 'published':
            phase = 'upcoming';
            break;
        case 'open':
            if (opens && now < opens)
                phase = 'upcoming';
            else if (!closes || now >= closes)
                phase = 'closed';
            else
                phase = 'open';
            break;
        case 'suspended':
            phase = 'suspended';
            break;
        case 'closed':
        case 'processing':
            phase = 'closed';
            break;
        default:
            phase = 'results';
    }
    return {
        phase,
        acceptsApplications: phase === 'open',
        opensAt: opens?.toISOString() ?? null,
        closesAt: closes?.toISOString() ?? null,
    };
}
const ACTION_LABELS = {
    'draft>published': 'Publier',
    'published>draft': 'Dépublier',
    'published>open': 'Ouvrir les dépôts',
    'open>suspended': 'Suspendre',
    'suspended>open': 'Reprendre',
    'open>closed': 'Clôturer les dépôts',
    'closed>processing': 'Passer en traitement',
    'final_results>archived': 'Archiver',
};
const DANGER = new Set(['published>draft', 'open>suspended', 'open>closed']);
/**
 * Actions manuelles possibles depuis `status` (hors système, hors résultats),
 * filtrées par la fonction d'autorisation fournie. La prolongation est une
 * action à part (nouvelle date de clôture).
 */
export function lifecycleActions(status, can = () => true) {
    return competitionMachine.transitions
        .filter((t) => t.from === status &&
        t.permission !== null &&
        !COMPETITION_TRANSITIONS_ELSEWHERE.includes(t.to) &&
        !(t.from === 'closed' && t.to === 'open') // prolongation : action dédiée
    )
        .filter((t) => can(t.permission))
        .map((t) => {
        const key = `${t.from}>${t.to}`;
        return {
            to: t.to,
            permission: t.permission,
            reasonRequired: t.reasonRequired,
            label: ACTION_LABELS[key] ?? t.to,
            tone: DANGER.has(key) ? 'danger' : 'primary',
        };
    });
}
/** La prolongation (ou réouverture après clôture) est possible dans ces états. */
export const COMPETITION_EXTENDABLE = ['open', 'closed'];
//# sourceMappingURL=competitions.js.map